import {
  MyFiltersProps,
  FormPrice,
  Price,
  Option,
  Options,
  ModalFilter
} from "~/components/Filters/MyFilters.props"
import { SliderValue } from "~/components/Slider.props"
import { formatMoney } from "~/utils/formatters"

export type Filters = Pick<
  MyFiltersProps,
  "price" | "brands" | "categories" | "availability" | "sortingItems"
>

export const FILTERS_MODAL = "filters-modal"
export const BRANDS_MODAL = "brands-modal"
export const CATEGORIES_MODAL = "categories-modal"
export const SORTING_MODAL = "sorting-modal"
export const MAGAZINE_CATEGORIES_MODAL = "magazine-categories-modal"

function instanceOfPrice(object: any): object is Price {
  return "min" in object
}

export const setOptionsDefaultValue = (options: Options): Options => {
  return Object.keys(options).reduce((acc: Options, brandName) => {
    if (typeof options[brandName] === "string") return acc
    acc[brandName] = false
    return acc
  }, {})
}

export const mergeOptionsWithValues = (
  sourceOptions: Option[],
  options: Options
): Option[] => {
  return Object.keys(options).reduce((acc: Option[], optionName) => {
    const option = sourceOptions?.find(({ name }) => name === optionName)
    if (!option) return acc
    acc.push({ ...option, checked: options[optionName] })
    return acc
  }, [])
}

export const getActiveOptions = (
  options: FormPrice,
  sourceOptions: Price | Option[]
): ModalFilter => {
  const names = Object.keys(options)?.reduce((acc: string[], option) => {
    if (
      typeof options[option as keyof typeof options] === "boolean" &&
      options[option as keyof typeof options]
    )
      acc.push(option)
    return acc
  }, [])

  let activeOptions: Pick<Option, "name" | "text">[] = []
  if (instanceOfPrice(sourceOptions)) {
    const range = options.range as SliderValue
    if (range.min !== sourceOptions.min || range.max !== sourceOptions.max)
      activeOptions.push({
        name: "price",
        text: `${formatMoney(range.min)} - ${formatMoney(range.max)}`
      })

    activeOptions = [
      ...activeOptions,
      ...sourceOptions.options
        .filter(({ name }) => names.includes(name))
        .map(({ name, text }) => ({ name, text }))
    ]
  }

  if (!instanceOfPrice(sourceOptions)) {
    activeOptions = sourceOptions
      .filter(({ name }) => names.includes(name))
      .map(({ name, text }) => ({ name, text }))
  }

  return { options: activeOptions, totalActive: activeOptions.length }
}

export const getAllActiveOptions = (options: {
  [key: string]: ModalFilter
}): number => {
  return Object.values(options).reduce(
    (acc: number, option) => (acc += option.totalActive),
    0
  )
}

export const selectOptions = (options: {
  [key: string]: boolean | string
}): void => {
  Object.keys(options).forEach((option) => {
    if (typeof options[option] === "boolean") options[option] = true
  })
}

export const resetOptions = (
  options: FormPrice | Options,
  price?: Price
): void => {
  Object.keys(options).forEach((option) => {
    const obj = options as Options

    if (option === "range" && price) {
      const priceRange = options.range as SliderValue
      priceRange.min = price.min
      priceRange.max = price.max
    }

    if (typeof obj[option] === "boolean") obj[option] = false
  })
}

const countFilersNumericActivated = (
  objectFilters:
    | {
        ">=": any
        "<=": any
      }
    | {}
) => {
  if (isEmpty(objectFilters)) return 0

  let counter = 0
  // @ts-ignore
  counter += objectFilters[">="]?.length ?? 0
  // @ts-ignore
  counter += objectFilters["<="]?.length ?? 0

  return counter
}

export const getFiltersNumbersByState = (
  state: any,
  facetName: string | string[] | undefined
): number => {
  let acc = 0

  if (!facetName) return 0

  const facetNames = Array.isArray(facetName) ? facetName : [facetName]

  facetNames.forEach((facetName) => {
    acc += state?.disjunctiveFacetsRefinements?.[facetName]?.length ?? 0
    if (state?.numericRefinements?.[facetName]) {
      acc += countFilersNumericActivated(
        state?.numericRefinements?.[facetName] ?? {}
      )
    }
  })

  return acc
}
